import { useEffect, useMemo, useState } from "react";
import { useParams, Link, Navigate } from "@/lib/router-compat";
import { allBlogPosts } from "@/lib/allBlogPosts";
import {
  deduplicatePosts,
  processBlogContent,
  sortByDateDesc,
} from "@/lib/blogPostProcessor";
import { injectContextualLinks } from "@/lib/contextualLinks";
import { generateFAQs, generateLastUpdated } from "@/lib/faqGenerator";
import {
  normalizePostDates,
  formatDisplayDate,
  getNormalizedDate,
} from "@/lib/dateNormalizer";
import { enhanceForGEO } from "@/lib/geoEnhancer";
import { categoryToSlug, getRelatedCategories } from "@/lib/categorySlug";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CustomCursor } from "@/components/CustomCursor";
import { MobileBottomBar } from "@/components/MobileBottomBar";
import { BlogPostSidebar } from "@/components/BlogPostSidebar";
import { ArticleReadingProgressBar } from "@/components/ArticleReadingProgressBar";
import { ArticleRelatedSection } from "@/components/ArticleRelatedSection";
import { useToast } from "@/hooks/use-toast";
import {
  Calendar,
  Clock,
  ChevronRight,
  Linkedin,
  Link2,
  ShieldCheck,
  Award,
  BookOpen,
  Check,
} from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const dedupedPosts = useMemo(
    () => sortByDateDesc(deduplicatePosts(allBlogPosts)),
    [],
  );
  const dateMap = useMemo(
    () => normalizePostDates(dedupedPosts),
    [dedupedPosts],
  );
  const post = dedupedPosts.find((p) => p.slug === slug);
  const normalizedDate = post ? getNormalizedDate(post, dateMap) : "";
  const displayDate = post ? formatDisplayDate(normalizedDate) : "";
  const generatedFAQs = useMemo(() => (post ? generateFAQs(post) : []), [post]);
  const generatedLastUpdated = useMemo(
    () => (post ? generateLastUpdated(post) : undefined),
    [post],
  );

  const topRankingArticles = useMemo(() => {
    return dedupedPosts.filter((p) => p.slug !== slug).slice(0, 8);
  }, [dedupedPosts, slug]);

  const relatedPosts = useMemo(() => {
    if (!post) return [];
    const sameCat = dedupedPosts.filter(
      (p) => p.category === post.category && p.slug !== post.slug,
    );
    const others = dedupedPosts.filter(
      (p) => p.category !== post.category && p.slug !== post.slug,
    );
    return [...sameCat, ...others].slice(0, 3);
  }, [post]);

  const postCategorySlug = useMemo(
    () => (post ? categoryToSlug(post.category) : ""),
    [post],
  );
  const relatedCategories = useMemo(() => {
    if (!post) return [];
    return getRelatedCategories(post.category, dedupedPosts, 2);
  }, [post, dedupedPosts]);

  const processedContent = useMemo(() => {
    if (!post) return "";
    let cleaned = processBlogContent(post.content, post.category);
    const escapedTitle = post.title.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    cleaned = cleaned.replace(
      new RegExp(`^\\s*<h1[^>]*>\\s*${escapedTitle}\\s*<\\/h1>\\s*`, "i"),
      "",
    );
    cleaned = cleaned.replace(
      new RegExp(`^\\s*${escapedTitle}\\s*(?=<p|<h[2-6]|<div|<ul|<ol)`, "i"),
      "",
    );
    const withLinks = injectContextualLinks(cleaned, post, dedupedPosts);
    return enhanceForGEO(withLinks, post.category);
  }, [post, dedupedPosts]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const shareUrl = `https://marjohnrobillo.me/blog/${post.slug}`;

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast({
      title: "Link copied!",
      description: "Share it with your team or engineering leads.",
    });
    setTimeout(() => setCopied(false), 2400);
  };

  return (
    <div className="min-h-screen bg-[#FBFDFF] text-foreground selection:bg-[#0B7CF2]/20 relative overflow-hidden">
      {/* Dynamic Reading Progress Bar at very top */}
      <ArticleReadingProgressBar />

      {/* Atmospheric pale-blue architectural backdrop matching Blueprint */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, #FFFFFF 0%, #F8FBFF 40%, #EFF7FF 100%)",
          }}
        />
        {/* Soft radial blue atmosphere */}
        <div
          className="absolute top-0 right-0 w-[800px] h-[600px] pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 75% 20%, rgba(11,124,242,0.07) 0%, rgba(38,182,255,0.02) 45%, transparent 70%)",
          }}
        />
        <div
          className="absolute top-[40%] left-[-150px] w-[600px] h-[600px] pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(11,124,242,0.05) 0%, transparent 65%)",
          }}
        />
      </div>

      <CustomCursor />
      <Navbar />
      <MobileBottomBar />

      <main className="pt-32 pb-24 px-4 sm:px-6 md:px-10 lg:px-12 max-w-[1520px] mx-auto relative z-10">
        {/* Breadcrumb Navigation with subtle whitepaper badge */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10 pb-4 border-b border-[#082E67]/10">
          <nav aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-xs font-mono flex-wrap text-[#6A7D97]">
              <li className="flex items-center gap-2">
                <Link to="/" className="hover:text-[#0B7CF2] transition-colors">
                  Home
                </Link>
                <ChevronRight className="w-3 h-3 text-[#6A7D97]" />
              </li>
              <li className="flex items-center gap-2">
                <Link
                  to="/blog"
                  className="hover:text-[#0B7CF2] transition-colors"
                >
                  Systems Log
                </Link>
                <ChevronRight className="w-3 h-3 text-[#6A7D97]" />
              </li>
              <li className="flex items-center gap-2">
                <Link
                  to={`/blog/category/${postCategorySlug}`}
                  className="hover:text-[#0B7CF2] transition-colors font-semibold text-[#0B7CF2]"
                >
                  {post.category}
                </Link>
                <ChevronRight className="w-3 h-3 text-[#6A7D97]" />
              </li>
              <li>
                <span
                  className="text-[#071E61] font-semibold truncate max-w-[220px] inline-block"
                  aria-current="page"
                >
                  {post.title}
                </span>
              </li>
            </ol>
          </nav>

          <div className="hidden sm:flex items-center gap-2 text-[11px] font-bold text-[#0B7CF2] bg-[#EDF7FF] px-3 py-1 rounded-full border border-[#0B7CF2]/20">
            <BookOpen className="w-3 h-3" />
            <span>ARCHITECTURAL WHITEPAPER</span>
          </div>
        </div>

        {/* 2-Column Responsive Layout: Left (Article) + Right (Sticky Architectural Sidebar) */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] xl:grid-cols-[1fr_390px] gap-8 xl:gap-12 items-start">
          {/* Main Article Column */}
          <div className="min-w-0">
            <article className="bg-white/90 backdrop-blur-xs border border-[#082E67]/10 rounded-3xl p-6 sm:p-10 md:p-12 shadow-[0_16px_48px_rgba(8,46,103,0.06)]">
              <header className="mb-12">
                {/* 1. Article Headline on Top */}
                <h1
                  className="font-display font-black mb-6 tracking-tight text-[#071E61]"
                  style={{
                    fontSize: "clamp(30px, 4.2vw, 50px)",
                    lineHeight: 1.15,
                    letterSpacing: "-0.035em",
                  }}
                >
                  {post.title}
                </h1>

                {/* 2. Below Title: Author / Architect Card with Professional Headshot */}
                <div className="flex flex-wrap items-center justify-between gap-4 border border-[#082E67]/10 py-4 bg-gradient-to-r from-[#F7FBFF] to-[#EFF7FF] rounded-2xl px-5 sm:px-6 my-6 shadow-[0_6px_20px_rgba(8,46,103,0.03)]">
                  <div className="flex items-center gap-3.5 sm:gap-4">
                    <div className="relative">
                      <img
                        src="https://vibe.filesafe.space/meta/1790177007883282700/favicon.webp"
                        alt="Marjohn B. Robillo"
                        width={52}
                        height={52}
                        loading="eager"
                        decoding="async"
                        fetchPriority="high"
                        className="w-13 h-13 rounded-full border-2 border-[#0B7CF2]/40 object-cover object-top shrink-0 shadow-xs block bg-white"
                      />
                      <span
                        className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#0B7CF2] text-white flex items-center justify-center ring-2 ring-white"
                        title="Verified Systems Architect"
                      >
                        <ShieldCheck className="w-3 h-3" />
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Link
                          to="/authors/marjohn-robillo"
                          className="font-bold text-[#071E61] text-[15px] sm:text-[16px] leading-snug hover:text-[#0B7CF2] transition-colors"
                          title="Marjohn Robillo — author profile"
                        >
                          Marjohn B. Robillo
                        </Link>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-[#EDF7FF] text-[#0B7CF2] border border-[#0B7CF2]/20">
                          Verified Architect
                        </span>
                      </div>
                      <div className="text-xs sm:text-[13px] text-[#536B89] font-medium leading-normal">
                        <Link
                          to="/authors/marjohn-robillo"
                          className="hover:text-[#0B7CF2] transition-colors"
                          title="Revenue Operations Architect profile"
                        >
                          Revenue Operations & AI Automation Architect
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Author Social & Share Tools */}
                  <div className="flex items-center gap-2">
                    <a
                      href="https://www.linkedin.com/in/marjohnrobillo-com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-white rounded-full border border-slate-200 transition-colors hover:bg-[#EDF7FF] hover:text-[#0B7CF2] text-[#6A7D97] shadow-xs inline-flex items-center justify-center"
                      aria-label="Marjohn Robillo on LinkedIn"
                      title="Connect on LinkedIn"
                    >
                      <Linkedin className="w-4 h-4" />
                    </a>
                    <button
                      onClick={copyShareLink}
                      className="p-2 bg-white rounded-full border border-slate-200 transition-colors hover:bg-[#EDF7FF] hover:text-[#0B7CF2] text-[#6A7D97] shadow-xs cursor-pointer inline-flex items-center justify-center"
                      aria-label="Copy article link"
                      title="Copy article link"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-[#169846]" />
                      ) : (
                        <Link2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* 3. Below Author: Date, Time & Metadata Row (Machine-Readable + User-Friendly) */}
                <div className="flex flex-wrap items-center gap-3 text-xs font-mono text-[#6A7D97]">
                  <Link
                    to={`/blog/category/${postCategorySlug}`}
                    className="bg-[#EDF7FF] text-[#0B7CF2] font-semibold px-3 py-1 rounded-full hover:bg-[#E0F2FE] transition-colors border border-[#0B7CF2]/20"
                  >
                    ● {post.category}
                  </Link>
                  <span className="flex items-center gap-1.5 bg-slate-50 px-3 py-1 rounded-full border border-slate-200/80">
                    <Calendar className="w-3.5 h-3.5 text-[#0B7CF2]" />{" "}
                    <time dateTime={post.date}>Published: {displayDate}</time>
                  </span>
                  {generatedLastUpdated && (
                    <span className="flex items-center gap-1.5 bg-[#EDF7FF]/70 text-[#071E61] px-3 py-1 rounded-full border border-[#0B7CF2]/20">
                      <Clock className="w-3.5 h-3.5 text-[#0B7CF2]" /> Modified:{" "}
                      <span>{generatedLastUpdated}</span>
                    </span>
                  )}
                  <span className="flex items-center gap-1.5 bg-slate-50 px-3 py-1 rounded-full border border-slate-200/80">
                    <BookOpen className="w-3.5 h-3.5 text-[#536B89]" />{" "}
                    {post.readTime}
                  </span>
                </div>
              </header>

              {/* Prose / Article Body with Enhanced Technical Whitepaper Styling */}
              <div
                className="prose prose-lg max-w-none prose-headings:font-display prose-headings:font-extrabold prose-headings:tracking-tight prose-headings:text-[#071E61] prose-h2:text-[24px] sm:prose-h2:text-[28px] prose-h2:pt-8 prose-h2:mt-12 prose-h2:mb-5 prose-h2:leading-[1.3] prose-h2:border-t prose-h2:border-[#082E67]/10 prose-h3:text-[20px] sm:prose-h3:text-[22px] prose-h3:mt-9 prose-h3:mb-4 prose-h3:leading-[1.35] prose-a:text-[#0B7CF2] prose-a:font-semibold hover:prose-a:text-[#0865C9] prose-a:underline prose-a:underline-offset-4 prose-a:decoration-[#0B7CF2]/40 hover:prose-a:decoration-[#0B7CF2] prose-p:text-[#334E68] prose-p:text-[17px] sm:prose-p:text-[18px] prose-p:leading-[1.85] prose-p:mb-6 prose-p:font-normal prose-li:text-[#334E68] prose-li:text-[17px] sm:prose-li:text-[18px] prose-li:leading-[1.8] prose-li:mb-2.5 prose-strong:text-[#071E61] prose-strong:font-bold prose-blockquote:border-l-4 prose-blockquote:border-[#0B7CF2] prose-blockquote:bg-[#F0F8FF] prose-blockquote:py-5 prose-blockquote:px-6 prose-blockquote:rounded-r-2xl prose-blockquote:not-italic prose-blockquote:text-[#082E67] prose-blockquote:my-8 prose-blockquote:shadow-xs prose-table:my-8 prose-table:w-full prose-table:border-collapse prose-th:bg-[#F0F8FF] prose-th:text-[#071E61] prose-th:p-3.5 prose-th:text-left prose-th:font-bold prose-td:p-3.5 prose-td:border-b prose-td:border-slate-100 prose-td:text-[#334E68] [&_a.bg-primary]:!text-foreground hover:[&_a.bg-primary]:!text-foreground"
                dangerouslySetInnerHTML={{ __html: processedContent }}
              />

              {/* Verified FAQs Accordion Section */}
              {generatedFAQs.length > 0 && (
                <section className="mt-16 pt-10 border-t border-[#082E67]/10">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2 h-2 rounded-full bg-[#27DC5D]" />
                    <span className="text-[11px] font-bold tracking-widest uppercase text-[#0B7CF2]">
                      AEO • DIRECT TECHNICAL ANSWERS
                    </span>
                  </div>
                  <h2
                    className="font-display font-extrabold text-[#071E61] mb-8"
                    style={{
                      fontSize: "clamp(24px, 2.6vw, 32px)",
                      letterSpacing: "-0.025em",
                    }}
                  >
                    Frequently Asked Questions
                  </h2>
                  <div className="space-y-4">
                    {generatedFAQs.map((faq, idx) => (
                      <div
                        key={idx}
                        className="bg-[#F8FCFF] border border-[#082E67]/10 rounded-2xl p-6 shadow-[0_8px_24px_rgba(8,42,102,0.03)]"
                      >
                        <h3 className="text-[17px] font-display font-bold text-[#071E61] mb-2.5">
                          {faq.question}
                        </h3>
                        <p className="leading-relaxed text-[15px] text-[#536B89]">
                          {faq.answer}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Tag Badges + Author Bio Footer Card */}
              <footer className="mt-16 pt-10 border-t border-[#082E67]/10">
                <div className="flex flex-wrap gap-2 mb-10">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-[#F4F9FD] border border-[#082E67]/10 rounded-full text-xs font-mono text-[#536B89]"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="bg-gradient-to-br from-[#F8FCFF] via-[#FFFFFF] to-[#EFF7FF] border border-[#0B7CF2]/20 rounded-2xl p-7 sm:p-9 flex flex-col sm:flex-row gap-6 items-start sm:items-center relative overflow-hidden shadow-[0_16px_40px_rgba(8,42,102,0.06)]">
                  <img
                    src="https://vibe.filesafe.space/meta/1790177007883282700/favicon.webp"
                    alt="Marjohn Robillo"
                    width={96}
                    height={96}
                    loading="lazy"
                    decoding="async"
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-2 border-[#0B7CF2]/30 object-cover shrink-0 shadow-sm block"
                    onError={(e) => {
                      const target = e.currentTarget;
                      if (
                        target.src !==
                        "https://storage.googleapis.com/msgsndr/pLFrnXnIu7IgQ4WCaCfz/media/f5f1f2f1-bbe0-4ac9-8f9d-f59083df9317.webp"
                      ) {
                        target.src =
                          "https://storage.googleapis.com/msgsndr/pLFrnXnIu7IgQ4WCaCfz/media/f5f1f2f1-bbe0-4ac9-8f9d-f59083df9317.webp";
                      }
                    }}
                  />
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Link
                        to="/authors/marjohn-robillo"
                        className="text-xl font-display font-black text-[#071E61] hover:text-[#0B7CF2] transition-colors"
                        title="Marjohn Robillo — author profile"
                      >
                        Marjohn B. Robillo
                      </Link>
                      <Award className="w-4 h-4 text-[#0B7CF2]" />
                    </div>
                    <p className="text-[#0B7CF2] text-xs font-bold uppercase tracking-wider mb-2.5">
                      <Link
                        to="/authors/marjohn-robillo"
                        className="hover:underline"
                        title="Revenue Operations Architect profile"
                      >
                        Revenue Operations & AI Automation Architect
                      </Link>
                    </p>
                    <p className="text-sm leading-relaxed text-[#536B89] max-w-2xl">
                      I build the systems that make sales teams faster, data
                      trustworthy, and operations run without manual
                      intervention, with 5+ years of experience for marketing
                      agencies, SaaS companies, and high-growth businesses
                      across the US, Canada, and Australia. 300+ CRM
                      environments built. 80% manual workload eliminated. Zero
                      tolerance for bad data.{" "}
                      <Link
                        to="/authors/marjohn-robillo"
                        className="text-[#0B7CF2] font-semibold hover:underline"
                        title="Read more about Marjohn Robillo"
                      >
                        Read the full profile →
                      </Link>
                    </p>
                  </div>
                </div>
              </footer>
            </article>
          </div>

          {/* Right Sticky Sidebar: Dynamic Table of Contents, Top Articles & Audit Widgets */}
          <BlogPostSidebar
            slug={post.slug}
            topRankingArticles={topRankingArticles}
            postTitle={post.title}
          />
        </div>

        {/* Bottom Related & Category Navigation */}
        <ArticleRelatedSection
          relatedPosts={relatedPosts}
          postCategory={post.category}
          postCategorySlug={postCategorySlug}
          relatedCategories={relatedCategories}
          dedupedPosts={dedupedPosts}
          categoryToSlug={categoryToSlug}
        />
      </main>

      <Footer />
    </div>
  );
}
